import { Component, ChangeDetectionStrategy, input, computed, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { EmptyContentComponent, UserAvatarComponent } from '@aionzdev/design-system';
import { PurchaseRequestAuditDto } from '../../../interfaces/purchase-request-audit.dto';
import { ACTIVITY_FILTERS, ActivityFilter, ActivityType, resolveActivityType } from './activity-types';
import { ACTIVITY_CHIP_ICONS, ActivitySegment, parseActivityMessage } from './activity-message';

interface ActivityView {
  activity: PurchaseRequestAuditDto;
  type: ActivityType;
  segments: ActivitySegment[];
}

interface ActivityGroup {
  dateLabel: string;
  activities: ActivityView[];
}

@Component({
  selector: 'aionz-purchase-request-activities-tab',
  imports: [
    CommonModule,
    MatIconModule,
    EmptyContentComponent,
    UserAvatarComponent
  ],
  templateUrl: './purchase-request-activities-tab.component.html',
  styleUrls: ['./purchase-request-activities-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchaseRequestActivitiesTabComponent {
  activities = input<PurchaseRequestAuditDto[]>([]);
  isLoading = input<boolean>(false);
  hasError = input<boolean>(false);

  protected readonly filters = ACTIVITY_FILTERS;
  protected readonly filter = signal<ActivityFilter>('all');

  /** Quantidade por filtro, para o chip mostrar o número e o usuário não clicar em filtro vazio. */
  protected readonly filterCounts = computed<Record<ActivityFilter, number>>(() => {
    const counts: Record<ActivityFilter, number> = { all: 0, quote: 0, suppliers: 0, items: 0, comments: 0, system: 0 };
    for (const activity of this.activities()) {
      counts.all++;
      counts[this.typeOf(activity).group]++;
    }
    return counts;
  });

  protected readonly chipIcons = ACTIVITY_CHIP_ICONS;

  protected readonly filteredActivities = computed<ActivityView[]>(() => {
    const current = this.filter();
    return this.activities()
      .map(activity => ({ activity, type: this.typeOf(activity), segments: parseActivityMessage(activity.message) }))
      .filter(view => current === 'all' || view.type.group === current);
  });

  protected typeOf(activity: PurchaseRequestAuditDto): ActivityType {
    return resolveActivityType(activity.action, activity.status);
  }

  protected setFilter(filter: ActivityFilter): void {
    this.filter.set(filter);
  }

  protected readonly groupedActivities = computed<ActivityGroup[]>(() => {
    const allActivities = this.filteredActivities();
    if (allActivities.length === 0) return [];

    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const groups = new Map<string, ActivityView[]>();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    for (const view of allActivities) {
      const date = new Date(view.activity.createdAt);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(view);
    }

    return Array.from(groups.entries()).map(([, activities]) => {
      const date = new Date(activities[0].activity.createdAt);
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      
      let dateLabel = `${day} ${month}`;
      if (year !== today.getFullYear()) {
        dateLabel += ` ${year}`;
      }
      
      if (compareDate.getTime() === today.getTime()) {
        dateLabel = `Hoje · ${dateLabel}`;
      } else if (compareDate.getTime() === yesterday.getTime()) {
        dateLabel = `Ontem · ${dateLabel}`;
      }

      return {
        dateLabel,
        activities
      };
    });
  });

  protected formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
}
